import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '../api';
import { unwrapData } from '@wapharma/shared';
import { getDisplayError } from '../apiErrors';
import { getToken } from '../auth';
import { useToast } from '../contexts/ToastContext';
import { isCatalogImportInboxType } from '../components/inboxCatalogImport';
import { dispatchCatalogImportSeen } from '../utils/catalogImportSeenStorage';
import { openInboxEntity } from '../lib/inboxOpenEntity';
import { useAuth } from '../contexts/AuthContext';

export type InboxItem = {
  id: string;
  type: string;
  entityType: string;
  entityId: string;
  severity: 'low' | 'medium' | 'high';
  whyNow: string;
  nextAction: string;
  ownerRole: string;
  /** SupportTicket.type when entity is a ticket (BUG | QUESTION | FEEDBACK). */
  entityKind?: string;
  seen: boolean;
  seenAt: string | null;
};

type InboxListResponse = {
  data: InboxItem[];
  meta?: { unseen?: number; total?: number };
};

export function inboxItemKey(i: Pick<InboxItem, 'ownerRole' | 'type' | 'entityId'>) {
  return `${i.ownerRole}|${i.type}|${i.entityId}`;
}

export function useInboxListState() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;
  const [items, setItems] = useState<InboxItem[]>([]);
  const [pending, setPending] = useState<string | null>(null);

  const reportError = useCallback((e: unknown) => {
    showToastRef.current(getDisplayError(e), { tone: 'error' });
  }, []);

  const load = useCallback(() => {
    const token = getToken();
    if (!token) return;
    void apiGet<InboxListResponse>(`/inbox`, token)
      .then((b) => {
        const list = unwrapData<InboxItem[]>(b);
        setItems(list);
      })
      .catch((e) => reportError(e));
  }, [reportError]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onReload = () => load();
    window.addEventListener('wapharma-inbox-reload', onReload);
    return () => window.removeEventListener('wapharma-inbox-reload', onReload);
  }, [load]);

  const markSeen = useCallback(
    async (i: Pick<InboxItem, 'ownerRole' | 'type' | 'entityId'>) => {
      const token = getToken();
      if (!token) return;
      const now = new Date().toISOString();
      setItems((prev) =>
        prev.map((row) =>
          row.ownerRole === i.ownerRole &&
          row.type === i.type &&
          row.entityId === i.entityId
            ? { ...row, seen: true, seenAt: now }
            : row,
        ),
      );
      if (i.ownerRole === 'catalog' && isCatalogImportInboxType(i.type)) {
        dispatchCatalogImportSeen(i.entityId);
      }
      try {
        await apiPost(
          `/inbox/seen`,
          {
            items: [
              {
                ownerRole: i.ownerRole,
                type: i.type,
                entityId: i.entityId,
              },
            ],
          },
          token,
        );
      } catch (e) {
        reportError(e);
        load();
      }
    },
    [load, reportError],
  );

  const snooze24h = async (i: InboxItem) => {
    const token = getToken();
    if (!token) return;
    setPending(i.id);
    const key = inboxItemKey(i);
    setItems((prev) => prev.filter((row) => inboxItemKey(row) !== key));
    const snoozedUntil = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    try {
      await apiPost(
        `/inbox/snooze`,
        { type: i.type, entityId: i.entityId, ownerRole: i.ownerRole, entityType: i.entityType, snoozedUntil },
        token,
      );
      await load();
    } catch (e: unknown) {
      reportError(e);
      load();
    } finally {
      setPending(null);
    }
  };

  const dismiss = async (i: InboxItem) => {
    const token = getToken();
    if (!token) return;
    setPending(i.id);
    const key = inboxItemKey(i);
    setItems((prev) => prev.filter((row) => inboxItemKey(row) !== key));
    try {
      await apiPost(
        `/inbox/dismiss`,
        { type: i.type, entityId: i.entityId, ownerRole: i.ownerRole, entityType: i.entityType },
        token,
      );
      await load();
    } catch (e: unknown) {
      reportError(e);
      load();
    } finally {
      setPending(null);
    }
  };

  const openEntity = (i: InboxItem) => {
    openInboxEntity(i, (path) => nav(path), {
      grants: user?.grants,
      roles: user?.roles,
      packs: user?.packs,
    });
  };

  const isJobSeen = useCallback(
    (jobId: string, type: string) =>
      items.some(
        (row) => row.entityId === jobId && row.type === type && row.ownerRole === 'catalog' && row.seen,
      ),
    [items],
  );

  return {
    items,
    pending,
    reload: load,
    snooze24h,
    dismiss,
    openEntity,
    markSeen,
    isJobSeen,
  };
}
